import { useEffect, useRef, useState } from "preact/hooks";
import { renderTemplate } from "@fobu/engine";
import { useFormEnv } from "../lib/context";
import { t } from "../lib/i18n";
import { Markdown } from "../lib/markdown";
import { Branding, LazyLive, Logo } from "./Common";
import { embedded, postToHost } from "../lib/embed";

const safeRedirect = (url: string) => {
  try {
    const u = new URL(url);
    return u.protocol === "https:" || u.protocol === "http:" ? u.toString() : null;
  } catch {
    return null;
  }
};

export function EndingView() {
  const { ctl } = useFormEnv();
  const ref = useRef<HTMLHeadingElement>(null);
  const result = ctl.ending!;
  const ctx = { answers: result.flow.answers, other: ctl.other, vars: result.flow.variables, hidden: result.flow.hidden };
  const text = (s: string) => renderTemplate(s, ctl.idx, ctx);
  // Zahlung offen: nicht wegleiten – erst bezahlen.
  const redirect = result.redirectUrl && !result.paymentToken && !result.resultsToken ? safeRedirect(result.redirectUrl) : null;

  useEffect(() => {
    ref.current?.focus();
    postToHost({ type: "submitted", waitlisted: !!result.waitlisted });
    // Eingebettet leitet die einbettende Seite weiter (ein iframe darf das nicht selbst).
    if (embedded && redirect && !result.waitlisted) {
      const t = setTimeout(() => postToHost({ type: "redirect", url: redirect }), 1800);
      return () => clearTimeout(t);
    }
    // Auf der Warteliste nicht wegleiten – der Hinweis ist wichtiger als die Dankeseite.
    if (!redirect || result.waitlisted) return;
    const timer = setTimeout(() => window.location.assign(redirect), 1800);
    return () => clearTimeout(timer);
  }, []);

  return (
    <div class={`fb-shell fb-shell--ending fb-shell--${ctl.def.settings.mode}`}>
      <Logo />
      <div class="fb-ending">
        <svg class="fb-ending-mark" viewBox="0 0 48 48" width="48" height="48" aria-hidden="true">
          <circle cx="24" cy="24" r="22" />
          <path d="M14 24.5l7 7L34 17" />
        </svg>
        <h1 class="fb-ending-title" tabIndex={-1} ref={ref}>
          <Markdown text={text(result.ending.title)} />
        </h1>
        {result.updated && (
          <p class="fb-banner fb-ending-notice" role="status">
            {t.updatedNotice}
          </p>
        )}
        {result.waitlisted && (
          <p class="fb-banner fb-ending-notice" role="status">
            {t.waitlistNotice}
          </p>
        )}
        {result.ending.body && (
          <div class="fb-ending-body">
            <Markdown text={text(result.ending.body)} as="p" />
          </div>
        )}
        {result.paymentToken && <LazyPayment token={result.paymentToken} />}
        {result.resultsToken && <LazyLive token={result.resultsToken} lang={ctl.def.settings.language} />}
        {redirect && (
          <p class="fb-ending-redirect" role="status">
            {t.redirecting} <a href={redirect}>{result.ending.buttonLabel ?? t.redirectLink}</a>
          </p>
        )}
      </div>
      <Branding />
    </div>
  );
}

export function MessageView({ title, body }: { title: string; body?: string }) {
  return (
    <div class="fb-shell fb-shell--ending">
      <div class="fb-ending">
        <h1 class="fb-ending-title">{title}</h1>
        {body && <p class="fb-ending-body">{body}</p>}
      </div>
    </div>
  );
}

/** Bezahlbereich als eigener Chunk – Formulare ohne Zahlung laden ihn nie. */
export function LazyPayment({ token, standalone }: { token: string; standalone?: boolean }) {
  const [View, setView] = useState<null | ((p: { token: string; standalone?: boolean }) => preact.JSX.Element)>(null);
  useEffect(() => {
    void import("./PaymentPanel").then((m) => setView(() => m.PaymentPanel));
  }, []);
  return View ? <View token={token} standalone={!!standalone} /> : <div class="fb-loading" aria-busy="true" />;
}
